import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import { SwitchToggleV2 } from 'core-system/SwitchToggle'
import Radio from 'core-system/Radio'
import { AppState } from 'redux/config/store'
import { AutoInviteSetting } from 'redux/employer/employerTypes'
import { RuleLogicEditor } from 'features/RuleBuilder'
import { firstFieldOtherThan } from 'features/RuleBuilder/ruleBuilderConstants'
import {
  RuleField,
  RuleOperator,
  ConditionJoin,
  hasConditionValue,
} from 'features/RuleBuilder'
import { autoInviteActions } from './autoInviteSlice'
import { AutoInviteScope, AutoInviteRule } from './autoInviteTypes'
import SyncSettingsCollapsible from 'features/Segments/syncSettings/SyncSettingsCollapsible'

const SettingRow = styled(FlexContainer)`
  padding: 1.5rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey300};

  &:last-child {
    border-bottom: none;
  }
`

const SettingLabel = styled.div`
  flex: 1;
`

const RadioOption = styled(FlexContainer)`
  cursor: pointer;
`

const CriteriaSurface = styled.div`
  margin-top: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  background: ${(props) => props.theme.palette.grey.grey100};
  padding: 1.35rem 1.5rem;
`

export interface AutoInviteSectionProps {
  autoInviteSetting: AutoInviteSetting
  onAutoInviteSettingChange: (setting: AutoInviteSetting) => void
}

const AutoInviteSection: React.FC<AutoInviteSectionProps> = ({
  autoInviteSetting,
  onAutoInviteSettingChange,
}) => {
  const dispatch = useDispatch()
  const rule = useSelector((state: AppState) => state.autoInvite.rule)
  const ruleLoaded = useSelector((state: AppState) => state.autoInvite.ruleLoaded)

  const [addSecondCondition, setAddSecondCondition] = useState(false)
  const [addThirdCondition, setAddThirdCondition] = useState(false)

  useEffect(() => {
    dispatch(autoInviteActions.getAutoInviteRule())
  }, [dispatch])

  useEffect(() => {
    if (!rule || rule.scope !== 'MATCHING') {
      setAddSecondCondition(false)
      setAddThirdCondition(false)
      return
    }
    const secOp = rule.secondaryOperator ?? 'equals'
    const hasSecond =
      Boolean(rule.secondaryField) &&
      hasConditionValue(rule.secondaryValue ?? '', secOp)
    const terOp = rule.tertiaryOperator ?? 'equals'
    const hasThird =
      hasSecond &&
      Boolean(rule.tertiaryField) &&
      hasConditionValue(rule.tertiaryValue ?? '', terOp)
    setAddSecondCondition(hasSecond)
    setAddThirdCondition(hasThird)
  }, [rule?.id, rule?.scope, ruleLoaded])

  const handleAutoInviteToggle = () => {
    if (autoInviteSetting === 'DISABLED') {
      onAutoInviteSettingChange('WITHOUT_EMAIL')
    } else {
      onAutoInviteSettingChange('DISABLED')
    }
  }

  const handleScopeChange = (scope: AutoInviteScope) => {
    dispatch(autoInviteActions.setScope(scope))
  }

  const patchRule = (changes: Partial<AutoInviteRule>) => {
    dispatch(autoInviteActions.updateLocalRule(changes))
  }

  const handleAddSecondConditionChange = (next: boolean) => {
    if (!next) {
      setAddThirdCondition(false)
      patchRule({
        secondaryField: null,
        secondaryOperator: null,
        secondaryValue: null,
        conditionJoin: null,
        tertiaryField: null,
        tertiaryOperator: null,
        tertiaryValue: null,
        tertiaryConditionJoin: null,
      })
    } else {
      patchRule({
        secondaryField: 'additional_info',
        secondaryOperator: 'equals',
        secondaryValue: '',
        conditionJoin: 'and',
      })
    }
    setAddSecondCondition(next)
  }

  const handleAddThirdConditionChange = (next: boolean) => {
    if (!next) {
      patchRule({
        tertiaryField: null,
        tertiaryOperator: null,
        tertiaryValue: null,
        tertiaryConditionJoin: null,
      })
    } else {
      patchRule({
        tertiaryField: 'title',
        tertiaryOperator: 'equals',
        tertiaryValue: '',
        tertiaryConditionJoin: 'and',
      })
    }
    setAddThirdCondition(next)
  }

  const isAutoInviteEnabled = autoInviteSetting !== 'DISABLED'
  const scope = rule?.scope ?? 'ALL_NEW'
  const field = (rule?.field ?? 'worksite_name') as RuleField
  const operator = (rule?.operator ?? 'equals') as RuleOperator
  const value = rule?.value ?? ''
  const secondaryField = (rule?.secondaryField ?? 'additional_info') as RuleField
  const secondaryOperator = (rule?.secondaryOperator ?? 'equals') as RuleOperator
  const secondaryValue = rule?.secondaryValue ?? ''
  const conditionJoin = (rule?.conditionJoin ?? 'and') as ConditionJoin
  const tertiaryField = (rule?.tertiaryField ?? 'title') as RuleField
  const tertiaryOperator = (rule?.tertiaryOperator ?? 'equals') as RuleOperator
  const tertiaryValue = rule?.tertiaryValue ?? ''
  const tertiaryConditionJoin = (rule?.tertiaryConditionJoin ??
    'and') as ConditionJoin

  useEffect(() => {
    if (!rule || rule.scope !== 'MATCHING' || !addSecondCondition) return
    if (secondaryField === field) {
      patchRule({
        secondaryField: firstFieldOtherThan(
          field,
          ...(addThirdCondition ? [tertiaryField] : [])
        ),
      })
    }
  }, [addSecondCondition, addThirdCondition, field, secondaryField, tertiaryField, rule?.scope])

  useEffect(() => {
    if (!rule || rule.scope !== 'MATCHING' || !addThirdCondition) return
    if (tertiaryField === field || tertiaryField === secondaryField) {
      patchRule({ tertiaryField: firstFieldOtherThan(field, secondaryField) })
    }
  }, [addThirdCondition, field, secondaryField, tertiaryField, rule?.scope])

  return (
    <SyncSettingsCollapsible
      title='Auto-Invite'
      status={isAutoInviteEnabled ? 'On' : 'Off'}
    >
      <SettingRow alignItems='center' justifyContent='space-between'>
        <SettingLabel>
          <Text variant='action3' marginBottom='0.25rem'>
            Auto-Invite
          </Text>
          <Text variant='body2' color='grey600'>
            Automatically invite newly added commuters when they are created.
            Rules run only for newly added commuters — not when existing
            profiles change.
          </Text>
        </SettingLabel>
        <SwitchToggleV2
          active={isAutoInviteEnabled}
          onClick={handleAutoInviteToggle}
          size='large'
        />
      </SettingRow>

      {isAutoInviteEnabled && (
        <>
          <SettingRow flexDirection='column'>
            <Text variant='action3' marginBottom='1rem'>
              Invite Method
            </Text>
            <FlexContainer flexDirection='column'>
              <RadioOption
                alignItems='flex-start'
                onClick={() => onAutoInviteSettingChange('WITH_EMAIL')}
                marginBottom='1.25rem'
              >
                <Radio
                  active={autoInviteSetting === 'WITH_EMAIL'}
                  label={
                    <FlexContainer flexDirection='column'>
                      <Text variant='action4'>Invite with Email</Text>
                      <Text variant='body2' color='grey600'>
                        Send an email invitation to new commuters
                      </Text>
                    </FlexContainer>
                  }
                />
              </RadioOption>
              <RadioOption
                alignItems='flex-start'
                onClick={() => onAutoInviteSettingChange('WITHOUT_EMAIL')}
              >
                <Radio
                  active={autoInviteSetting === 'WITHOUT_EMAIL'}
                  label={
                    <FlexContainer flexDirection='column'>
                      <Text variant='action4'>Invite without Email</Text>
                      <Text variant='body2' color='grey600'>
                        Add commuters without sending an email notification
                      </Text>
                    </FlexContainer>
                  }
                />
              </RadioOption>
            </FlexContainer>
          </SettingRow>

          <SettingRow flexDirection='column'>
            <Text variant='action3' marginBottom='1rem'>
              Who to invite
            </Text>
            <FlexContainer flexDirection='column'>
              <RadioOption
                alignItems='flex-start'
                onClick={() => handleScopeChange('ALL_NEW')}
                marginBottom='1.25rem'
              >
                <Radio
                  active={scope === 'ALL_NEW'}
                  label={
                    <FlexContainer flexDirection='column'>
                      <Text variant='action4'>All new commuters</Text>
                      <Text variant='body2' color='grey600'>
                        Invite every commuter added through sync or import
                      </Text>
                    </FlexContainer>
                  }
                />
              </RadioOption>
              <RadioOption
                alignItems='flex-start'
                onClick={() => handleScopeChange('MATCHING')}
              >
                <Radio
                  active={scope === 'MATCHING'}
                  label={
                    <FlexContainer flexDirection='column'>
                      <Text variant='action4'>
                        Only commuters matching criteria
                      </Text>
                      <Text variant='body2' color='grey600'>
                        Invite only when profile data matches your conditions
                      </Text>
                    </FlexContainer>
                  }
                />
              </RadioOption>
            </FlexContainer>

            {scope === 'MATCHING' && ruleLoaded && rule && (
              <CriteriaSurface>
                <Text variant='body2' color='grey600' fontWeight={600} marginBottom='0.85rem'>
                  Match criteria
                </Text>
                <RuleLogicEditor
                  field={field}
                  operator={operator}
                  value={value}
                  onFieldChange={(f) => patchRule({ field: f })}
                  onOperatorChange={(o) => patchRule({ operator: o })}
                  onValueChange={(v) => patchRule({ value: v })}
                  addSecondCondition={addSecondCondition}
                  onAddSecondConditionChange={handleAddSecondConditionChange}
                  conditionJoin={conditionJoin}
                  onConditionJoinChange={(j) => patchRule({ conditionJoin: j })}
                  secondaryField={secondaryField}
                  secondaryOperator={secondaryOperator}
                  secondaryValue={secondaryValue}
                  onSecondaryFieldChange={(f) => patchRule({ secondaryField: f })}
                  onSecondaryOperatorChange={(o) =>
                    patchRule({ secondaryOperator: o })
                  }
                  onSecondaryValueChange={(v) => patchRule({ secondaryValue: v })}
                  addThirdCondition={addThirdCondition}
                  onAddThirdConditionChange={handleAddThirdConditionChange}
                  tertiaryConditionJoin={tertiaryConditionJoin}
                  onTertiaryConditionJoinChange={(j) =>
                    patchRule({ tertiaryConditionJoin: j })
                  }
                  tertiaryField={tertiaryField}
                  tertiaryOperator={tertiaryOperator}
                  tertiaryValue={tertiaryValue}
                  onTertiaryFieldChange={(f) => patchRule({ tertiaryField: f })}
                  onTertiaryOperatorChange={(o) =>
                    patchRule({ tertiaryOperator: o })
                  }
                  onTertiaryValueChange={(v) => patchRule({ tertiaryValue: v })}
                />
              </CriteriaSurface>
            )}
          </SettingRow>
        </>
      )}
    </SyncSettingsCollapsible>
  )
}

export default React.memo(AutoInviteSection)
